import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  agendaDoPeriodo, avisosDeEncomenda, contasVencendoAteHoje, encomendasVencendoEm,
  filaDeConferencia, listarFiados, listarImpressoes, listarPedidos, metasProgresso,
  orcamentosVencendoAte, pedidosB2BNovos, promocoesTerminandoAte, saudeDaFila,
  vendasPorVendedor,
} from '../lib/consultas';
import { hojeISO } from '../lib/formato';
import {
  alternarConferida, CHAVES_DO_SINO, contarNovas, marcarLidas, mesclarRegistro,
  montarNotificacoes, ocultarNotificacao, paraExibir,
  type RegistroNotificacao,
} from '../lib/notificacoes';
import { usePainelSuspenso } from '../lib/painelSuspenso';
import {
  podeConferirCadastro, podeGerenciarVendas, podeVerFaturamento, podeVerFinancas, useSessao,
} from '../lib/sessao';

// O SINO (17/08/2026): ao lado do nome, em toda tela. O que ele sabe dizer
// mora em lib/notificacoes.ts (puro, testado); aqui é só buscar os dados de
// tempos em tempos e lembrar, NO APARELHO, o estado de cada aviso.
//
// v2 (18/08/2026, pedido do dono): cada aviso tem três gestos — LER (abrir o
// painel deixa o aviso com opacidade baixa), CONFERIR (✓: "dei conta") e
// OCULTAR (✕: some de vez) — e o que sai do ar fica UMA SEMANA de histórico,
// a não ser que a pessoa oculte. Tudo em localStorage: trocar de aparelho
// zera o sino, nunca os dados.
//
// Cadência: a cada 10 min, e no foco da aba. Meta não vira notícia em
// segundos — vira no fim de uma venda.

const CHAVE_REGISTRO = 'sino-registro-v2';

/** Quanto o painel precisa ficar aberto para valer como LIDO. */
const ESPERA_PARA_LER_MS = 400;

function lerRegistro(): RegistroNotificacao[] {
  try {
    const bruto = localStorage.getItem(CHAVE_REGISTRO);
    const lista = bruto ? JSON.parse(bruto) as unknown : [];
    if (!Array.isArray(lista)) return [];
    return lista.filter((x): x is RegistroNotificacao =>
      typeof x === 'object' && x !== null
      && typeof (x as RegistroNotificacao).id === 'string'
      && typeof (x as RegistroNotificacao).visto_em === 'string');
  } catch {
    return [];
  }
}

function salvarRegistro(registro: RegistroNotificacao[]): void {
  try { localStorage.setItem(CHAVE_REGISTRO, JSON.stringify(registro)); } catch { /* cheio */ }
}

export default function Sino() {
  const { perfil, papel } = useSessao();
  const gerencia = podeGerenciarVendas(papel);
  // DUAS RÉGUAS, e a diferença importa desde 28/09/2026 (mesmo corpo hoje):
  //   verFaturamento → quanto se VENDE (meta, contribuição, pedido do parceiro)
  //   verFinancas    → o CUSTO e o RESULTADO (conta a pagar vencendo)
  // O sino usava a segunda para as três primeiras. Funcionava por coincidência:
  // as duas são `p === 'dono'` hoje. No dia em que uma delas afrouxar — e é
  // exatamente para esse dia que as duas existem separadas —, o aviso de meta
  // seguiria a régua errada e o faturamento voltaria pelo sino.
  const verFaturamento = podeVerFaturamento(papel);
  const dono = podeVerFinancas(papel);
  const conferir = podeConferirCadastro(papel);
  const [registro, setRegistro] = useState<RegistroNotificacao[]>(lerRegistro);

  const agora = new Date();
  const ano = agora.getFullYear();
  const trimestre = Math.floor(agora.getMonth() / 3) + 1;
  const hoje = hojeISO();

  const CADENCIA = { staleTime: 10 * 60_000, refetchInterval: 10 * 60_000 } as const;
  const amanhaISO = (() => {
    const d = new Date(`${hoje}T12:00:00`); d.setDate(d.getDate() + 1); return hojeISO(d);
  })();
  const em2diasISO = (() => {
    const d = new Date(`${hoje}T12:00:00`); d.setDate(d.getDate() + 2); return hojeISO(d);
  })();

  // META E CONTRIBUIÇÃO: só quem vê faturamento (28/09/2026). Não é só
  // esconder o aviso — a consulta nem sai do aparelho, porque o banco passou a
  // negar as duas para a equipe, e pedido negado a cada 10 min viraria erro
  // vermelho no sino de quem está atendendo.
  const progresso = useQuery({
    queryKey: ['sino', 'metas', ano, trimestre],
    queryFn: () => metasProgresso(ano, trimestre),
    ...CADENCIA, enabled: verFaturamento && Boolean(perfil?.id),
  });

  const contribuicao = useQuery({
    queryKey: ['sino', 'contribuicao', ano, trimestre],
    queryFn: async () => {
      const meses = [0, 1, 2].map((i) =>
        `${ano}-${String((trimestre - 1) * 3 + 1 + i).padStart(2, '0')}-01`);
      const linhas = await Promise.all(meses.map((m) => vendasPorVendedor(m)));
      return linhas.flat()
        .filter((v) => v.vendedor_id === perfil?.id)
        .reduce((s, v) => s + Number(v.total_vendido), 0);
    },
    ...CADENCIA, enabled: verFaturamento && Boolean(perfil?.id),
  });

  const agenda = useQuery({
    queryKey: ['sino', 'agenda', hoje],
    queryFn: () => agendaDoPeriodo(hoje, amanhaISO),
    ...CADENCIA, enabled: Boolean(perfil?.id),
  });

  // --- Os avisos operacionais. Papel gateado aqui E na RLS (o daqui poupa a
  // viagem; o de lá é o que vale).
  const contas = useQuery({
    queryKey: CHAVES_DO_SINO.contas(hoje),
    queryFn: () => contasVencendoAteHoje(hoje),
    ...CADENCIA, enabled: dono,
  });
  const fiados = useQuery({
    queryKey: ['sino', 'fiados'],
    queryFn: () => listarFiados(),
    ...CADENCIA, enabled: gerencia,
  });
  const pedidos = useQuery({
    queryKey: ['sino', 'pedidos'],
    // false de propósito: pedido RECEBIDO POR INTEIRO sai do filtro "abertos",
    // mas pode ter cliente a avisar — é exatamente o caso que mais importa.
    queryFn: () => listarPedidos(false),
    ...CADENCIA, enabled: gerencia,
  });
  const orcamentos = useQuery({
    queryKey: ['sino', 'orcamentos', hoje],
    queryFn: () => orcamentosVencendoAte(hoje, amanhaISO),
    ...CADENCIA, enabled: Boolean(perfil?.id),
  });
  const promos = useQuery({
    queryKey: ['sino', 'promos', hoje],
    queryFn: () => promocoesTerminandoAte(hoje, em2diasISO),
    ...CADENCIA, enabled: gerencia,
  });
  const filaImpressao = useQuery({
    queryKey: ['sino', 'fila-impressao'],
    queryFn: saudeDaFila,
    ...CADENCIA, enabled: gerencia,
  });
  const impressoes = useQuery({
    queryKey: ['sino', 'impressoes'],
    queryFn: () => listarImpressoes(20),
    ...CADENCIA, enabled: gerencia,
  });
  // Encomenda vencendo HOJE (18/08/2026): todos — a RLS entrega ao vendedor só
  // as encomendas dele; à gerência, todas.
  const encomendas = useQuery({
    queryKey: CHAVES_DO_SINO.encomendas(hoje),
    queryFn: () => encomendasVencendoEm(hoje),
    ...CADENCIA, enabled: Boolean(perfil?.id),
  });

  // Recado de colega (050): chega a quem foi nomeado, ou a todos. Cadência
  // mais curta — recado atrasado 10 min é recado perdido no balcão.
  const recados = useQuery({
    queryKey: ['sino', 'recados', perfil?.id],
    queryFn: () => avisosDeEncomenda(perfil!.id),
    staleTime: 3 * 60_000, refetchInterval: 3 * 60_000,
    enabled: Boolean(perfil?.id),
  });

  // Pedido de parceiro (062): cadência curta como a dos recados — parceiro
  // esperando confirmação 10 min é parceiro ligando para o balcão.
  // Só dono desde 28/09/2026: a lista de pedidos do parceiro saiu da vista do
  // funcionário junto com o resto do faturamento, e pedir ao servidor o que a
  // policy nega só renderia uma consulta vazia de 3 em 3 minutos.
  const pedidosB2B = useQuery({
    queryKey: ['sino', 'pedidos-b2b'],
    queryFn: pedidosB2BNovos,
    staleTime: 3 * 60_000, refetchInterval: 3 * 60_000,
    enabled: verFaturamento,
  });

  // Peça cadastrada pelo vendedor esperando liberação (093). Só para quem
  // libera: o vendedor vê as dele na própria lista, e o aviso de "N peças
  // esperando" levaria a uma tela onde ele não tem botão.
  const paraLiberar = useQuery({
    queryKey: ['sino', 'para-liberar'],
    queryFn: () => filaDeConferencia('pendente'),
    ...CADENCIA, enabled: conferir && Boolean(perfil?.id),
  });

  const atuais = useMemo(() => montarNotificacoes({
    progresso: progresso.data ?? [],
    minhaContribuicao: contribuicao.data ?? 0,
    // Dito duas vezes de propósito (aqui e no `enabled` das consultas): se um
    // dia a resposta vier do cache de outro login, o aviso de meta ainda não
    // nasce para quem não pode ver faturamento.
    verFaturamento,
    agenda: agenda.data ?? [],
    hoje,
    pedidosB2B: pedidosB2B.data ?? [],
    contas: contas.data ?? [],
    fiados: fiados.data ?? [],
    encomendasAvisar: (pedidos.data ?? []).filter((pd) => !pd.cancelado_em),
    orcamentosVencendo: orcamentos.data ?? [],
    promosTerminando: promos.data ?? [],
    impressoesFalhas: (impressoes.data ?? []).filter((j) => j.status === 'falha'),
    impressoesParadas: filaImpressao.data?.parados_ha_10min ?? 0,
    encomendasVencendo: encomendas.data ?? [],
    recadosDeEncomenda: recados.data ?? [],
    // A tela da fila (19/08/2026) é o destino certo: dá para mandar de novo
    // e cancelar ali mesmo.
    paraImpressao: '/impressoes',
    // Dito duas vezes, como o verFaturamento: resposta de cache de outro
    // login não faz o aviso nascer para quem não libera.
    pecasParaLiberar: conferir ? (paraLiberar.data ?? []).length : 0,
  }), [progresso.data, contribuicao.data, agenda.data, hoje, contas.data,
       fiados.data, pedidos.data, orcamentos.data, promos.data, impressoes.data,
       encomendas.data, recados.data, filaImpressao.data, pedidosB2B.data, papel,
       verFaturamento, conferir, paraLiberar.data]);

  // O que está no ar entra no REGISTRO; o que saiu fica uma semana. O merge é
  // idempotente — rodar de novo com os mesmos dados não muda nada.
  useEffect(() => {
    setRegistro((r) => {
      const mesclado = mesclarRegistro(atuais, r, hoje);
      salvarRegistro(mesclado);
      return mesclado;
    });
  }, [atuais, hoje]);

  const exibir = paraExibir(registro);
  const novas = contarNovas(registro);

  const aplicar = (proximo: RegistroNotificacao[]) => {
    salvarRegistro(proximo);
    setRegistro(proximo);
  };

  const { aberto, caixa, fechar, alternar, aoAproximar, aoAfastar } = usePainelSuspenso();

  // LER é DEMORAR no painel, não passar por cima dele (01/09/2026). Agora que
  // o sino abre com o mouse por cima, o caminho até o nome do usuário — ali do
  // lado — passa por aqui. Zerar o badge nesse esbarrão apagaria aviso que
  // ninguém leu, que é justamente o que o sino existe para não deixar
  // acontecer. Quem abre e FICA, leu.
  useEffect(() => {
    if (!aberto || novas === 0) return;
    const conta = window.setTimeout(() => {
      // Forma funcional: nesses 400ms uma consulta pode ter trazido aviso
      // novo, e marcar por cima de uma cópia velha o apagaria sem ser visto.
      setRegistro((r) => {
        const lido = marcarLidas(r);
        salvarRegistro(lido);
        return lido;
      });
    }, ESPERA_PARA_LER_MS);
    return () => window.clearTimeout(conta);
  }, [aberto, novas]);

  return (
    <div ref={caixa} className="relative"
         onPointerEnter={aoAproximar} onPointerLeave={aoAfastar}>
      <button type="button" onClick={alternar}
              aria-label={novas > 0 ? `Notificações: ${novas} nova(s)` : 'Notificações'}
              aria-haspopup="menu" aria-expanded={aberto}
              className="relative grid h-11 w-11 place-items-center rounded-lg border border-edge-forte bg-panel-alt text-mute hover:bg-edge hover:text-paper">
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M10.3 21a2 2 0 0 0 3.4 0" />
        </svg>
        {novas > 0 && (
          <span className="numero absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-paper px-1 text-[0.625rem] font-semibold text-ink">
            {novas}
          </span>
        )}
      </button>

      {aberto && (
        <div className="absolute right-0 z-40 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-edge bg-panel shadow-xl">
          {exibir.length === 0 ? (
            <p className="p-4 text-xs text-dim">
              Nada por enquanto. O sino avisa meta batida, evento da loja, conta
              a pagar, fiado antigo, encomenda chegada e vencendo, orçamento
              vencendo, promoção terminando e impressão que falhou — cada um só
              para quem pode ver. O que sai do ar fica 7 dias aqui, a não ser
              que você oculte no ✕.
            </p>
          ) : (
            <ul className="max-h-[min(60vh,24rem)] divide-y divide-edge overflow-y-auto">
              {exibir.map((n) => (
                <li key={n.id} className={`flex items-start gap-1 px-2 py-2 ${
                  n.lida ? 'opacity-50' : ''}`}>
                  <Link to={n.para} onClick={fechar}
                        className="min-w-0 flex-1 px-1 py-0.5 hover:bg-panel-alt">
                    <p className={`text-sm font-semibold leading-snug ${
                      n.conferida ? 'text-dim' : ''}`}>
                      {n.titulo}
                    </p>
                    {n.detalhe && <p className="mt-0.5 text-xs text-dim">{n.detalhe}</p>}
                    {n.visto_em !== hoje && (
                      <p className="mt-0.5 text-[0.625rem] text-dim">
                        desde {n.visto_em.split('-').reverse().slice(0, 2).join('/')}
                      </p>
                    )}
                  </Link>
                  {/* Os dois gestos: ✓ conferi (alterna), ✕ oculta de vez. */}
                  <button type="button"
                          aria-label={n.conferida ? 'Desmarcar conferida' : 'Marcar como conferida'}
                          aria-pressed={n.conferida}
                          onClick={() => aplicar(alternarConferida(registro, n.id))}
                          className={`shrink-0 border px-2 py-1 text-xs font-bold ${
                            n.conferida ? 'border-ok bg-ok/15 text-ok' : 'border-edge text-dim hover:text-ok'}`}>
                    ✓
                  </button>
                  <button type="button" aria-label="Ocultar notificação"
                          onClick={() => aplicar(ocultarNotificacao(registro, n.id))}
                          className="shrink-0 border border-edge px-2 py-1 text-xs font-bold text-dim hover:text-erro">
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
